export const LUGGAGE_OPTION_LABELS = ["大行李箱", "胖胖箱", "登機箱"] as const;

export type LuggageOptionLabel = (typeof LUGGAGE_OPTION_LABELS)[number];

export type LuggageSelection = {
  quantities: Record<LuggageOptionLabel, number>;
  selected: Record<LuggageOptionLabel, boolean>;
  otherSelected: boolean;
  otherNote: string;
};

const createDefaultQuantities = (): Record<LuggageOptionLabel, number> => ({
  大行李箱: 1,
  胖胖箱: 1,
  登機箱: 1,
});

const createDefaultSelected = (): Record<LuggageOptionLabel, boolean> => ({
  大行李箱: false,
  胖胖箱: false,
  登機箱: false,
});

export const createEmptyLuggageSelection = (): LuggageSelection => ({
  quantities: createDefaultQuantities(),
  selected: createDefaultSelected(),
  otherSelected: false,
  otherNote: "",
});

export const buildLuggagesString = (selection: LuggageSelection): string => {
  const lines: string[] = [];

  LUGGAGE_OPTION_LABELS.forEach((label) => {
    if (!selection.selected[label]) {
      return;
    }

    const quantity = Number.isFinite(selection.quantities[label]) && selection.quantities[label] > 0
      ? selection.quantities[label]
      : 1;
    lines.push(`${label} x ${quantity}`);
  });

  if (selection.otherSelected) {
    const otherNote = selection.otherNote.trim();
    lines.push(otherNote ? `其他：${otherNote}` : "其他");
  }

  return lines.length > 0 ? `${lines.join("\n")}\n` : "";
};

export const parseLuggagesString = (value?: string | null): LuggageSelection => {
  const selection = createEmptyLuggageSelection();
  const raw = value?.trim();

  if (!raw) {
    return selection;
  }

  const otherNotes: string[] = [];

  raw
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .forEach((line) => {
      let matchedLabel = false;

      LUGGAGE_OPTION_LABELS.forEach((label) => {
        if (!line.startsWith(label)) {
          return;
        }

        matchedLabel = true;
        selection.selected[label] = true;
        const quantityMatch = line.match(/(\d+)/);
        selection.quantities[label] = quantityMatch ? Math.max(1, Number(quantityMatch[1])) : 1;
      });

      if (matchedLabel) {
        return;
      }

      if (line.startsWith("其他")) {
        selection.otherSelected = true;
        const otherNote = line.replace(/^其他[:：]?\s*/, "").trim();
        if (otherNote) {
          otherNotes.push(otherNote);
        }
        return;
      }

      otherNotes.push(line);
      selection.otherSelected = true;
    });

  selection.otherNote = otherNotes.join("\n");
  return selection;
};
